import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useOutletContext, useSearchParams } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';

import {
  DEFAULT_HISTORY_SUBTAB,
  DRAFT_INTENTS,
  HISTORY_NEW_FORMS,
  HISTORY_NEW_PARAM,
  HISTORY_SUBTABS,
} from '../../constants/history.js';
import { useConciergeHandoff } from '../../hooks/useConciergeHandoff.js';
import NoteFormSlideOut from '../notes/NoteFormSlideOut.jsx';
import RoutedSubtabSwitch from '../RoutedSubtabSwitch.jsx';
import HistoryToolbar from './HistoryToolbar.jsx';
import LogInteractionSlideOut from './LogInteractionSlideOut.jsx';

/**
 * The shell of History (CT-5724), shared by Timeline and From notes: the
 * subtab switch, the toolbar (New note, Log call, Log meeting, Draft) and the
 * forms it opens. Each subtab places the switch and the toolbar in its own row.
 *
 * The open form belongs to the record it was opened on: the page stays mounted
 * across records, so a form left open when the user moves on (a link, browser
 * Back) closes instead of saving on the next record with this one's text.
 *
 * After a note or an interaction is saved, `feedVersion` moves on and the
 * Timeline refreshes quietly. `?new=note|call|meeting` (CT-5715's empty-state
 * actions) opens that form once and is dropped from the URL.
 */
function HistoryLayout() {
  const { t } = useTranslation('unified-profiles');
  const profileContext = useOutletContext();
  // `conciergeEntity` is the record as the page names it ("Untitled" included) —
  // the same one the Next Best Action card and Tasks hand the Concierge.
  const { entityType, entityId, conciergeEntity } = profileContext;
  const entityName = conciergeEntity?.title ?? '';
  const [searchParams, setSearchParams] = useSearchParams();
  const recordKey = `${entityType}/${entityId}`;
  const [openForm, setOpenForm] = useState(null); // { kind, recordKey }
  // Another record: the form is CLOSED, not just hidden — coming back does not
  // reopen it ("adjust state while rendering", no effect).
  if (openForm && openForm.recordKey !== recordKey) setOpenForm(null);
  const form = openForm?.recordKey === recordKey ? openForm.kind : null;
  const [feedVersion, setFeedVersion] = useState(0);
  const { available: canDraft, openConcierge } = useConciergeHandoff();

  // `?new=…` opens its form once — "adjust state while rendering", not an effect
  // — and the effect only drops the request from the URL.
  const requested = searchParams.get(HISTORY_NEW_PARAM);
  const [handledRequest, setHandledRequest] = useState(null);
  if (requested && requested !== handledRequest) {
    setHandledRequest(requested);
    if (HISTORY_NEW_FORMS.includes(requested)) setOpenForm({ kind: requested, recordKey });
  }
  // Once the link has left the URL, the same link again is a new request (Bugbot, #569).
  if (!requested && handledRequest) setHandledRequest(null);
  useEffect(() => {
    if (!requested) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(HISTORY_NEW_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [requested, setSearchParams]);

  const openNote = useCallback(() => setOpenForm({ kind: 'note', recordKey }), [recordKey]);
  const openCall = useCallback(() => setOpenForm({ kind: 'call', recordKey }), [recordKey]);
  const openMeeting = useCallback(() => setOpenForm({ kind: 'meeting', recordKey }), [recordKey]);
  const closeForm = useCallback(() => setOpenForm(null), []);
  const bumpFeed = useCallback(() => setFeedVersion((value) => value + 1), []);

  const handleDraft = useCallback(
    (channel) => openConcierge({ intent: DRAFT_INTENTS[channel], entity: conciergeEntity }),
    [openConcierge, conciergeEntity],
  );

  const subtabSwitch = useMemo(
    () => (
      <RoutedSubtabSwitch
        subtabs={HISTORY_SUBTABS}
        defaultSubtab={DEFAULT_HISTORY_SUBTAB}
        labelPrefix="history.subtabs"
        ariaLabel={t('history.subtabs.ariaLabel')}
        widthClassName="w-[220px]"
      />
    ),
    [t],
  );
  const toolbar = useMemo(
    () => (
      <HistoryToolbar
        canDraft={canDraft}
        onNewNote={openNote}
        onLogCall={openCall}
        onLogMeeting={openMeeting}
        onDraft={handleDraft}
      />
    ),
    [canDraft, openNote, openCall, openMeeting, handleDraft],
  );

  const context = useMemo(
    () => ({ ...profileContext, subtabSwitch, toolbar, feedVersion }),
    [profileContext, subtabSwitch, toolbar, feedVersion],
  );

  return (
    <>
      <Outlet context={context} />
      <NoteFormSlideOut
        isOpen={form === 'note'}
        onClose={closeForm}
        entityType={entityType}
        entityId={entityId}
        onSaved={bumpFeed}
        subtitle={t('history.newNote.subtitle', { name: entityName })}
      />
      {form === 'call' || form === 'meeting' ? (
        <LogInteractionSlideOut
          kind={form}
          entityType={entityType}
          entityId={entityId}
          entityName={entityName}
          onClose={closeForm}
          onLogged={bumpFeed}
        />
      ) : null}
    </>
  );
}

export default HistoryLayout;
